import LBox from "@components/LBox";
import LIcon from "@components/LIcon";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import { Access } from "@hooks/trpc/useAccesses";
import type { AccessGemEconomicsQuoteWire } from "@libs/wire";
import { type Dispatch, type SetStateAction, useEffect, useMemo } from "react";
import { NumberSelector } from "../tickets/NumberSelector";
import {
  createOptimisticMusicAccessGemQuote,
  formatGbpMinorUnits,
  formatGemQuantity,
  getCheckoutAmountBreakdown,
  getGemCalculationText,
  isGemPaymentAvailable,
} from "@libs/shared-ui";

type OrderSummaryProps = {
  /** An event off the engine (old rail): music unlocks in its own sheet. */
  access: Extract<Access, { type: "event" }>;
  numberOfTickets: number;
  setNumberOfTickets?: Dispatch<SetStateAction<number>>;
  pricePerTicketParam?: number;
  setTotalAmount: (amount: number) => void;
  setPricePerTicket?: (price: number) => void;
  setDirectPaymentCopy?: (copy: string | null) => void;
  gemEconomicsQuote?: AccessGemEconomicsQuoteWire | null;
  useGemsSelected: boolean;
  onUseGemsChange: (useGems: boolean) => void;
};

const GEM_PURPLE = "#C084FC";
const GEM_PURPLE_TEXT = "#E9D5FF";
const GEM_PURPLE_BACKGROUND = "rgba(126, 87, 194, 0.18)";
const GEM_PURPLE_BORDER = "rgba(168, 85, 247, 0.55)";

export default function OrderSummary({
  access,
  numberOfTickets,
  setNumberOfTickets,
  pricePerTicketParam,
  setTotalAmount,
  setPricePerTicket,
  setDirectPaymentCopy,
  gemEconomicsQuote,
  useGemsSelected,
  onUseGemsChange,
}: OrderSummaryProps) {
  const bookingFee = access.bookingFee ?? 0;

  // Determine price per ticket from params or access object
  const pricePerTicket = useMemo(() => {
    if (pricePerTicketParam !== undefined) {
      return pricePerTicketParam - bookingFee;
    }

    return access.price ?? 0;
  }, [pricePerTicketParam, access, bookingFee]);

  // Calculate totals
  const ticketTotal = pricePerTicket * numberOfTickets;
  const bookingFeeTotal = bookingFee * numberOfTickets;
  const subtotal = ticketTotal + bookingFeeTotal;
  const subtotalMinorUnits = Math.round(subtotal * 100);
  const fallbackGemEconomicsQuote = useMemo(
    () =>
      createOptimisticMusicAccessGemQuote({
        quote: gemEconomicsQuote,
        subtotalMinorUnits,
        useGemsSelected,
      }),
    [gemEconomicsQuote, subtotalMinorUnits, useGemsSelected],
  );
  const optimisticGemEconomicsQuote = fallbackGemEconomicsQuote;
  const amountBreakdown = getCheckoutAmountBreakdown({
    quote: fallbackGemEconomicsQuote,
    fallbackTotalMinorUnits: subtotalMinorUnits,
    useGemsSelected,
  });
  const shouldShowGemPayment = isGemPaymentAvailable(
    optimisticGemEconomicsQuote,
  );
  const paidAmountDirectCopy =
    amountBreakdown.originalMinorUnits > 0
      ? `Your ${amountBreakdown.originalAmount} goes directly to ${access.artistName}, not a pool`
      : null;

  useEffect(() => {
    setTotalAmount(amountBreakdown.amountDueMinorUnits / 100);
    if (setPricePerTicket) {
      setPricePerTicket(pricePerTicket + bookingFee);
    }
    setDirectPaymentCopy?.(paidAmountDirectCopy);
  }, [
    amountBreakdown.amountDueMinorUnits,
    paidAmountDirectCopy,
    setTotalAmount,
    pricePerTicket,
    bookingFee,
    setPricePerTicket,
    setDirectPaymentCopy,
  ]);

  return (
    <LBox gap="s4">
      <LBox
        p="s4"
        borderRadius="m-l"
        backgroundColor="whiteTranslucent10"
        borderColor="whiteBorder"
        borderWidth={1}
        gap="s4"
      >
        <LBox
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          gap="s3"
        >
          <LText variant="Heading/H2/SemiBold" color="whiteTranslucent70">
            Quantity
          </LText>
          {setNumberOfTickets ? (
            <NumberSelector
              number={numberOfTickets}
              setNumber={setNumberOfTickets}
            />
          ) : (
            <LText variant="Body/B1/SemiBold" color="white">
              {numberOfTickets}
            </LText>
          )}
        </LBox>

        <LBox flexDirection="row" justifyContent="space-between" gap="s3">
          <LText variant="Body/B2/Regular" color="whiteTranslucent50" flex={1}>
            {`${numberOfTickets} × ${formatGbpMinorUnits(
              Math.round((pricePerTicket + bookingFee) * 100),
            )}`}
          </LText>
          <LText variant="Body/B2/Medium" color="whiteTranslucent70">
            {formatGbpMinorUnits(subtotalMinorUnits)}
          </LText>
        </LBox>
      </LBox>

      <LBox gap="s3">
        <LText
          variant="Body/B2/SemiBold"
          color="whiteTranslucent70"
          textTransform="uppercase"
        >
          Pay with
        </LText>

        {shouldShowGemPayment && optimisticGemEconomicsQuote ? (
          <LPressable
            sentryEventName="AccessCheckout.ToggleGems"
            accessibilityRole="checkbox"
            accessibilityState={{ checked: useGemsSelected }}
            onPress={() => onUseGemsChange(!useGemsSelected)}
          >
            <LBox
              p="s4"
              borderRadius="m-l"
              borderWidth={1}
              gap="s3"
              style={{
                backgroundColor: useGemsSelected
                  ? GEM_PURPLE_BACKGROUND
                  : "rgba(255, 255, 255, 0.06)",
                borderColor: useGemsSelected
                  ? GEM_PURPLE_BORDER
                  : "rgba(255, 255, 255, 0.2)",
              }}
            >
              <LBox
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                gap="s3"
              >
                <LBox flexDirection="row" alignItems="center" gap="s3" flex={1}>
                  <LIcon name="gem" size={26} />
                  <LBox flex={1}>
                    <LText variant="Heading/H2/SemiBold" color="white">
                      {formatGemQuantity(
                        useGemsSelected
                          ? optimisticGemEconomicsQuote.gemQuantity ||
                              optimisticGemEconomicsQuote.availableGemQuantity
                          : optimisticGemEconomicsQuote.availableGemQuantity,
                      )}{" "}
                      gems
                    </LText>
                    <LText variant="Body/B2/Medium" color="whiteTranslucent70">
                      {formatGemQuantity(
                        optimisticGemEconomicsQuote.spendableGemsConsidered,
                      )}{" "}
                      in wallet
                    </LText>
                  </LBox>
                </LBox>

                <LBox
                  width={28}
                  height={28}
                  borderRadius="max"
                  alignItems="center"
                  justifyContent="center"
                  borderWidth={2}
                  style={{
                    borderColor: useGemsSelected
                      ? GEM_PURPLE
                      : "rgba(255, 255, 255, 0.45)",
                    backgroundColor: useGemsSelected
                      ? "rgba(192, 132, 252, 0.16)"
                      : "transparent",
                  }}
                >
                  {useGemsSelected ? (
                    <LIcon
                      colors={{ fill: GEM_PURPLE, mark: GEM_PURPLE }}
                      name="check"
                      size={14}
                    />
                  ) : null}
                </LBox>
              </LBox>

              <LBox borderTopWidth={1} borderColor="whiteBorder" />

              <LBox flexDirection="row" justifyContent="space-between" gap="s3">
                <LText
                  variant="Body/B2/SemiBold"
                  color="whiteTranslucent80"
                  flex={1}
                >
                  {getGemCalculationText(
                    optimisticGemEconomicsQuote,
                    useGemsSelected,
                  )}
                </LText>
                <LText
                  variant="Body/B1/SemiBold"
                  style={{ color: GEM_PURPLE_TEXT }}
                >
                  {formatGbpMinorUnits(
                    -(
                      optimisticGemEconomicsQuote.appliedDiscountMinorUnits ||
                      optimisticGemEconomicsQuote.availableDiscountMinorUnits
                    ),
                  )}
                </LText>
              </LBox>
            </LBox>
          </LPressable>
        ) : null}
      </LBox>
    </LBox>
  );
}

export function DirectPaymentCopy({ copy }: { copy: string }) {
  return (
    <LBox
      p="s4"
      borderRadius="m-l"
      borderWidth={1}
      flexDirection="row"
      gap="s3"
      alignItems="center"
      style={{
        backgroundColor: "rgba(16, 185, 129, 0.12)",
        borderColor: "rgba(16, 185, 129, 0.35)",
      }}
    >
      <LIcon colors={{ color: "#86EFAC" }} name="hearts" size={22} />
      <LText variant="Body/B1/SemiBold" style={{ color: "#A7F3D0" }} flex={1}>
        {copy}
      </LText>
    </LBox>
  );
}
